import { stripSuggestionsBlock } from './suggestion-chips';
/**
 * Turn an AI-SDK `message.parts` array into an ORDERED list of renderable
 * segments, preserving the chronological interleaving of text, reasoning, and
 * tool-call parts exactly as the model emitted them.
 *
 * Why this exists: the previous renderer flattened parts by type (all text into
 * one string, all reasoning into one, all tools into one array) and drew them in
 * a fixed order — reasoning, then tools, then text — which destroyed the
 * interleaving (the model emits thought → tools → text → thought → tools …).
 * Iterating `parts` in order is the canonical AI-SDK rendering pattern and fixes
 * the "all thought, then all tools, then all text" grouping bug.
 *
 * Rules:
 * - consecutive text parts (and consecutive reasoning parts) are coalesced into
 *   one segment, so streamed deltas don't fragment into many tiny blocks;
 * - a tool is rendered once, at the position where it was first invoked, but
 *   shows its LATEST state (a later occurrence of the same toolCallId updates it
 *   in place rather than adding a duplicate);
 * - non-render parts (data-*, step-start, file, source, legacy tool-invocation)
 *   are ignored;
 * - a text/reasoning segment whose COALESCED text is whitespace-only is dropped.
 *
 * Each segment carries a STABLE `id` (tools → toolCallId; text/reasoning → the
 * index of the part that started the run). Renderers must key off `id`, not the
 * array position, so React doesn't remount blocks (and reset e.g. the reasoning
 * panel's expand state) as the list grows during streaming.
 */

export type ToolState = 'input-streaming' | 'input-available' | 'output-available' | 'output-error';

export interface ToolPart {
  type: string; // 'tool-{toolName}', e.g. 'tool-read_files'
  toolCallId: string;
  state: ToolState;
  input?: Record<string, any>;
  output?: any;
  errorText?: string;
}

export type RenderSegment =
  | { kind: 'text'; id: string; text: string }
  | { kind: 'reasoning'; id: string; text: string }
  | { kind: 'tool'; id: string; part: ToolPart };

export function buildOrderedSegments(parts: readonly any[] | undefined | null): RenderSegment[] {
  // Next-step suggestions stream as a text block but render as chips — never as text.
  return buildRawSegments(parts)
    .map((segment) => (segment.kind === 'text' ? { ...segment, text: stripSuggestionsBlock(segment.text) } : segment))
    .filter((segment) => segment.kind !== 'text' || segment.text.length > 0);
}

function buildRawSegments(parts: readonly any[] | undefined | null): RenderSegment[] {
  const segments: RenderSegment[] = [];
  const toolIndexById = new Map<string, number>();
  let idx = -1;

  for (const part of parts ?? []) {
    idx++;

    const type: string | undefined = part?.type;

    if (!type) {
      continue;
    }

    if (type === 'text') {
      const text = part.text ?? '';
      const last = segments[segments.length - 1];

      if (last && last.kind === 'text') {
        // Separate text parts are separate messages from separate steps (one
        // narration line each) — glued with '' they read "site.Your guide…".
        last.text += last.text && text && !/\s$/.test(last.text) && !/^\s/.test(text) ? `\n\n${text}` : text;
      } else {
        segments.push({ kind: 'text', id: `text-${idx}`, text });
      }

      continue;
    }

    if (type === 'reasoning') {
      const text = part.reasoning ?? part.text ?? '';
      const last = segments[segments.length - 1];

      if (last && last.kind === 'reasoning') {
        last.text += text;
      } else {
        segments.push({ kind: 'reasoning', id: `reasoning-${idx}`, text });
      }

      continue;
    }

    if (type.startsWith('tool-') && type !== 'tool-invocation') {
      // Boundary cast: AI-SDK parts are loosely typed; we assert the tool shape
      // here at the one controlled point rather than letting `any` flow on.
      const toolPart = part as ToolPart;
      const id: string | undefined = toolPart.toolCallId ?? (part as any).id;

      if (id && toolIndexById.has(id)) {
        // Same tool call seen again (state advanced) — update in place, keeping
        // both its original position AND its original stable id.
        const at = toolIndexById.get(id)!;
        segments[at] = { kind: 'tool', id: segments[at].id, part: toolPart };
      } else {
        if (id) {
          toolIndexById.set(id, segments.length);
        }

        segments.push({ kind: 'tool', id: id ? `tool-${id}` : `tool-${idx}`, part: toolPart });
      }

      continue;
    }

    // ignore data-*, step-start, file, source, tool-invocation, etc.
  }

  return segments.filter((s) => (s.kind === 'tool' ? true : s.text.trim().length > 0));
}
